/**
 * Página de preview dos templates de email.
 * Acessível via /premiado/emails-preview
 */
import { useState } from 'react';
import {
  gerarEmailConfirmacaoCadastro,
  gerarEmailBoasVindas,
  gerarEmailConfirmacaoTransacao,
} from '@/components/emails';

type Tab = 'otp' | 'boasvindas' | 'transacao';

type ViewMode = 'desktop' | 'mobile';

interface TemplateInfo {
  key: Tab;
  label: string;
  descricao: string;
  icon: string;
  cor: string;
  bgCor: string;
  borderCor: string;
  badge: string;
  meta: { label: string; valor: string }[];
}

const templates: TemplateInfo[] = [
  {
    key: 'otp',
    label: 'Confirmação de Cadastro',
    descricao: 'Enviado durante o cadastro com o código OTP de 6 dígitos para validação do e-mail do usuário.',
    icon: 'ri-shield-check-line',
    cor: '#EA580C',
    bgCor: '#FFF7ED',
    borderCor: '#FED7AA',
    badge: 'OTP',
    meta: [
      { label: 'Destinatário', valor: 'Maria Silva Santos' },
      { label: 'E-mail', valor: 'maria.silva@email.com' },
      { label: 'Código OTP', valor: '472819' },
      { label: 'Expira em', valor: '10 minutos' },
    ],
  },
  {
    key: 'boasvindas',
    label: 'Boas-Vindas',
    descricao: 'Enviado após conclusão do cadastro, com resumo da conta, botão de acesso e próximos passos.',
    icon: 'ri-user-heart-line',
    cor: '#16A34A',
    bgCor: '#F0FDF4',
    borderCor: '#BBF7D0',
    badge: 'Boas-Vindas',
    meta: [
      { label: 'Destinatário', valor: 'Maria Silva Santos' },
      { label: 'E-mail', valor: 'maria.silva@email.com' },
      { label: 'CPF', valor: '123.456.789-00' },
      { label: 'Tipo', valor: 'Email permanente' },
    ],
  },
  {
    key: 'transacao',
    label: 'Confirmação de Transação',
    descricao: 'Enviado após cada transação financeira, com detalhes do valor, data, ID e saldo atualizado.',
    icon: 'ri-exchange-funds-line',
    cor: '#7C3AED',
    bgCor: '#F5F3FF',
    borderCor: '#DDD6FE',
    badge: 'Transação',
    meta: [
      { label: 'Destinatário', valor: 'Maria Silva Santos' },
      { label: 'Tipo', valor: 'Resgate de Recompensa' },
      { label: 'Valor', valor: 'R$ 150,00' },
      { label: 'Saldo Atual', valor: 'R$ 2.340,00' },
      { label: 'CPF (mascarado)', valor: '***.456.789-**' },
      { label: 'Chave PIX', valor: 'E-mail: maria.silva@email.com' },
      { label: 'Dados Bancários', valor: 'BB · Ag.1234-5 · C/C 12345-6' },
    ],
  },
];

export default function EmailsPreviewPage() {
  const [tab, setTab] = useState<Tab>('otp');
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');

  const htmlOTP = gerarEmailConfirmacaoCadastro({
    nome: 'Maria Silva Santos',
    codigoOTP: '472819',
    email: 'maria.silva@email.com',
    minutosExpiracao: 10,
  });

  const htmlBoasVindas = gerarEmailBoasVindas({
    nome: 'Maria Silva Santos',
    email: 'maria.silva@email.com',
    documento: '123.456.789-00',
  });

  const htmlTransacao = gerarEmailConfirmacaoTransacao({
    nome: 'Maria Silva Santos',
    tipo: 'resgate',
    valor: 150.0,
    moeda: 'BRL',
    data: '27/05/2026',
    hora: '14:32',
    idTransacao: 'TX-20260527-8A4F2C1B',
    descricao: 'Resgate - Vale Alimentação R$ 150,00',
    saldoAtual: 2340.0,
    minutosExpiracao: 90,
    destinatario: {
      nome: 'Maria Silva Santos',
      documento: '***.456.789-**',
      chavePix: 'maria.silva@email.com',
      tipoChavePix: 'E-mail',
      dadosBancarios: {
        banco: 'Banco do Brasil',
        agencia: '1234-5',
        conta: '12345-6',
        tipoConta: 'Corrente',
      },
    },
  });

  const htmlAtual = tab === 'otp' ? htmlOTP : tab === 'boasvindas' ? htmlBoasVindas : htmlTransacao;
  const templateAtual = templates.find((t) => t.key === tab)!;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-rose-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                <i className="ri-mail-send-line text-2xl text-white w-6 h-6 flex items-center justify-center"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Templates de Email</h1>
                <p className="text-sm text-slate-500">GetPoints — Visualize e teste os templates antes de implementar</p>
              </div>
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
              <button
                onClick={() => setViewMode('desktop')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  viewMode === 'desktop'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <i className="ri-computer-line w-4 h-4 flex items-center justify-center"></i>
                Desktop
              </button>
              <button
                onClick={() => setViewMode('mobile')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  viewMode === 'mobile'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <i className="ri-smartphone-line w-4 h-4 flex items-center justify-center"></i>
                Mobile
              </button>
            </div>
          </div>

          {/* Template Selector Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
            {templates.map((t) => {
              const ativo = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`group relative text-left p-4 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
                    ativo
                      ? 'border-orange-500 bg-white shadow-lg shadow-orange-500/10'
                      : 'border-transparent bg-white hover:border-slate-200 hover:shadow-md'
                  }`}
                >
                  {/* Badge */}
                  <span
                    className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3 ${
                      ativo ? 'text-white' : 'text-slate-500 bg-slate-100 group-hover:bg-slate-200'
                    }`}
                    style={ativo ? { backgroundColor: t.cor } : {}}
                  >
                    {t.badge}
                  </span>

                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
                      style={{ backgroundColor: ativo ? t.bgCor : '#F1F5F9' }}
                    >
                      <i
                        className={`${t.icon} text-lg w-5 h-5 flex items-center justify-center transition-colors`}
                        style={{ color: ativo ? t.cor : '#94A3B8' }}
                      ></i>
                    </div>
                    <div className="min-w-0">
                      <h3
                        className={`text-sm font-bold truncate transition-colors ${
                          ativo ? 'text-slate-800' : 'text-slate-600'
                        }`}
                      >
                        {t.label}
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5 line-clamp-2 leading-relaxed">
                        {t.descricao}
                      </p>
                    </div>
                  </div>

                  {/* Active indicator */}
                  {ativo && (
                    <div
                      className="absolute top-3 right-3 w-2 h-2 rounded-full"
                      style={{ backgroundColor: t.cor }}
                    ></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Metadata Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-[148px] space-y-5">
              {/* Template Info Card */}
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                {/* Header com cor do template */}
                <div
                  className="px-5 py-4 flex items-center gap-3"
                  style={{ backgroundColor: templateAtual.bgCor }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: templateAtual.cor }}
                  >
                    <i className={`${templateAtual.icon} text-xl text-white w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <div>
                    <h3
                      className="text-sm font-bold"
                      style={{ color: templateAtual.cor }}
                    >
                      {templateAtual.label}
                    </h3>
                    <p className="text-xs opacity-70" style={{ color: templateAtual.cor }}>
                      Template HTML responsivo
                    </p>
                  </div>
                </div>

                {/* Dados do email */}
                <div className="p-5 space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                      Dados do Template
                    </p>
                    <div className="space-y-0">
                      {templateAtual.meta.map((item, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-b-0"
                        >
                          <span className="text-xs text-slate-500 font-medium">{item.label}</span>
                          <span className="text-xs font-semibold text-slate-800 text-right max-w-[180px] truncate">
                            {item.valor}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Estatísticas visuais */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                      Características
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-lg font-bold text-slate-700">
                          {tab === 'boasvindas' ? '∞' : (tab === 'otp' ? '10min' : '90d')}
                        </p>
                        <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                          {tab === 'boasvindas' ? 'Sem expiração' : 'Prazo de expiração'}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-lg font-bold text-slate-700">600px</p>
                        <p className="text-[10px] text-slate-400 font-medium mt-0.5">Largura máxima</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-lg font-bold text-green-600">
                          <i className="ri-checkbox-circle-fill w-4 h-4 inline-flex items-center justify-center"></i>
                        </p>
                        <p className="text-[10px] text-slate-400 font-medium mt-0.5">Responsivo</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-lg font-bold text-green-600">
                          <i className="ri-checkbox-circle-fill w-4 h-4 inline-flex items-center justify-center"></i>
                        </p>
                        <p className="text-[10px] text-slate-400 font-medium mt-0.5">Dark mode safe</p>
                      </div>
                    </div>
                  </div>

                  {/* Compatibilidade */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                      Compatível com
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {['Gmail', 'Outlook', 'Apple Mail', 'Yahoo', 'Thunderbird'].map((client) => (
                        <span
                          key={client}
                          className="px-2.5 py-1 bg-slate-100 rounded-lg text-[10px] font-medium text-slate-500"
                        >
                          {client}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Dica rápida */}
              <div className="bg-gradient-to-br from-orange-50 to-rose-50 rounded-2xl border border-orange-100 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-white w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-orange-800 mb-1">Dica</p>
                    <p className="text-xs text-orange-600 leading-relaxed">
                      Os templates usam tabelas HTML com estilos inline para máxima compatibilidade com clientes de email.
                      Teste sempre em múltiplos dispositivos antes de enviar.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="lg:col-span-2">
            {viewMode === 'desktop' ? (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-green-400"></span>
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Preview Desktop</span>
                  </div>
                  <div className="flex-1 border-t border-slate-200"></div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xl shadow-slate-200/50">
                  {/* Browser chrome */}
                  <div className="bg-slate-100 px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                    <span className="w-3 h-3 rounded-full bg-red-400"></span>
                    <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                    <span className="w-3 h-3 rounded-full bg-green-400"></span>
                    <div className="flex-1 mx-4">
                      <div className="bg-white rounded-lg px-4 py-1.5 text-xs text-slate-400 truncate text-center">
                        getpoints.com.br — {templateAtual.label}
                      </div>
                    </div>
                  </div>
                  <iframe
                    key={`desktop-${tab}`}
                    srcDoc={htmlAtual}
                    title="Email Desktop Preview"
                    className="w-full border-0 bg-[#F1F5F9]"
                    style={{ height: '700px' }}
                  />
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-green-400"></span>
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Preview Mobile · 375px</span>
                  </div>
                  <div className="flex-1 border-t border-slate-200"></div>
                </div>
                <div className="flex justify-center">
                  <div className="bg-slate-800 rounded-[44px] p-3 shadow-2xl shadow-slate-300/80">
                    <div className="bg-white rounded-[32px] overflow-hidden w-[375px]" style={{ height: '700px' }}>
                      {/* Notch area */}
                      <div className="bg-slate-800 h-8 flex items-center justify-center rounded-t-[32px] relative">
                        <div className="w-24 h-6 bg-slate-800 rounded-b-2xl absolute top-0"></div>
                        <span className="text-[9px] font-semibold text-slate-500 mt-3">getpoints.com.br</span>
                      </div>
                      <iframe
                        key={`mobile-${tab}`}
                        srcDoc={htmlAtual}
                        title="Email Mobile Preview"
                        className="w-full border-0"
                        style={{ height: '672px' }}
                      />
                    </div>
                    {/* Home indicator */}
                    <div className="flex justify-center mt-3">
                      <div className="w-32 h-1 bg-slate-600 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Código HTML (collapsible) */}
            <details className="mt-6 group">
              <summary className="flex items-center gap-2 px-5 py-3 bg-white rounded-xl border border-slate-200 cursor-pointer hover:border-slate-300 transition-colors">
                <i className="ri-code-line text-slate-400 w-4 h-4 flex items-center justify-center"></i>
                <span className="text-sm font-semibold text-slate-600">Ver código HTML</span>
                <span className="text-xs text-slate-400 ml-auto">{htmlAtual.length.toLocaleString()} caracteres</span>
                <i className="ri-arrow-down-s-line text-slate-400 w-4 h-4 flex items-center justify-center group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="mt-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">HTML</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(htmlAtual)}
                    className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 rounded-lg text-[10px] text-slate-300 font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-file-copy-line w-3 h-3 flex items-center justify-center"></i>
                    Copiar
                  </button>
                </div>
                <pre className="p-5 text-xs text-slate-600 font-mono leading-relaxed overflow-auto max-h-[400px] whitespace-pre-wrap break-all">
                  {htmlAtual}
                </pre>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
}